<template>
  <div :id="'group-widget-' + campaignId"
       class="group-widget-section group-filters"
       :class="{'with-ad-filter': hasAdFilters}">
    <div :class="['group-widget', widgetOpenClass]">
      <div :class="['filters-header', {['slide-open']: slideDown}]" @click="toggleWidget">
        <div class="filter-header-text">
          <span class="recipients" data-cy="recipients-filter-list">{{ showCount ? 'recipients' : this.headerText || "filters" }}</span>
          <span v-if="showCount" :class="['total-recipients', selectedView]">
            (<loading-dots v-if="countLoading"></loading-dots>
            <counter :isLoading="countLoading" :updatedValue="totalCount"></counter>)
          </span>
        </div>
      </div>

      <groups-drop-down :campaignType="campaignType"
                        :groupsWithSelected="groupsWithSelected"
                        :isCampaign="isCampaign"
                        :isUpdating="updating || scopeUpdating"
                        :openGroupItem="openGroupItem"
                        :sectionOpen="sectionOpen"
                        :selectedDateRange="selectedDateRange"
                        :selectedMerchantSegmentIds="merchantSegmentIds"
                        :showCount="showCount"
                        :slideDown="slideDown"
                        :subConfigDate="subConfigDate"
                        :subConfigsGroupNames="subConfigsGroupNames"
                        @closeDropDown="closeDropDown"
                        @closeSelectedGroup="closeSection"
                        @setSelectedDateRange="setSelectedDateRange"
                        @setSelectedItem="setSelectedItem"
                        @updateSelectedGroups="updateGroups"></groups-drop-down>
    </div>

    <div class="selected-groups">
      <div v-if="errorMessage" class="error-label">{{errorMessage}}</div>
      <div class="group-name-list">
        <group-names :key="filterKey"
                     :displayDate="displayDate"
                     :groups="filterGroups || onlySelectedGroups"
                     :subConfigsGroupNames="subConfigsGroupNames"
                     isOnlySelected></group-names>
      </div>
    </div>
  </div>
</template>

<script>
  import axiosConfig from '../../config/axios'
  import Counter from './Counter'
  import Group from './Group'
  import GroupsDropDown from './GroupsDropDown'
  import GroupNames from './GroupNames'
  import { isObject } from '../../utilities'
  import LoadingDots from '../shared/LoadingDots'
  import OnlySelectedGroups from '../../mixins/OnlySelectedGroupsMixin'
  import SubConfigDate from '../../mixins/SubConfigDateMixin'

  export default {
    props: {
      campaignId: [Number, String],
      campaignType: String,
      hideCarriersGroup: Boolean,
      openGroupItem: [Number, String],
      scopeMerchantTagIds: Array,
      scopeSelectedSegmentIds: Array,
      scopeSelectedGroups: Array,
      scopeUpdating: Boolean,
      selectedView: String,
      showCount: Boolean,
      headerText: String,
      adFilters: {
        default: () => null,
        type: Object
      }
    },

    name: 'GroupWidget',

    components: {
      Counter,
      Group,
      GroupsDropDown,
      GroupNames,
      LoadingDots,
    },

    mixins: [OnlySelectedGroups, SubConfigDate],

    data() {
      return {
        allFilters: null,
        countLoading: false,
        emailTotal: null,
        errorMessage: null,
        filterKey: 0,
        isCampaign: null,
        merchantSegmentIds: null,
        merchantTagIds: null,
        subConfigMax: null,
        subConfigMin: null,
        sectionOpen: false,
        selectedItems: {},
        selectedDateRange: null,
        selectedGroupsUrl: null,
        slideDown: false,
        smsTotal: null,
        totalCount: 0,
        updating: false,
      };
    },

    methods: {
      closeDropDown(event) {
        if (!this.eventFromGroupFlatpickr(event.path)) {
          this.closeSection()
          this.slideDown = false
        }
      },

      closeSection() {
        this.sectionOpen = false
        this.$emit('updateData', 'openGroupItem', null)
      },

      configTotalCounts(countItem) {
        if (!countItem) {
          this.getTotalCount()
        } else {
          this.totalCount = countItem
        }
      },

      eventFromGroupFlatpickr(paths) {
        if (paths) {
          let pathClasses = paths.map(path => path.classList),
            isGroupPicker = false
          pathClasses.forEach((classArray) => {
            if (classArray && classArray.contains('group-picker')) { return isGroupPicker = true }
          })
          return isGroupPicker
        }
      },

      getTotalCount() {
        this.countLoading = true
        const isEmail = this.selectedView === 'email'
        let errorMessage = `could not load the total count for ${this.selectedView}`,
          url = `/api/v3/campaigns/${this.campaignId}/total_count`
        url = `${url}?key=${isEmail ? 'email' : 'stashboard'}`
        url = isEmail ? `${url}&email_total=true` : url
        axiosConfig.get(url)
          .then((response) => {
            const data = response.data
            if (isObject(data)) {
              this.totalCount = data.total_count
              if (isEmail) {
                this.emailTotal = data.total_count
              } else {
                this.smsTotal = data.total_count
              }
              this.$emit('updateData', 'recipientCount', this[`${isEmail ? 'email' : 'sms'}Total`])
              errorMessage = ''
            }
            this.setErrorMessage(errorMessage)
            this.countLoading = false
          })
          .catch((error) => {
            errorMessage = `${errorMessage}. ${error}`
            this.setErrorMessage(errorMessage)
            this.countLoading = false
          })
      },

      groupsCall(callType, params) {
        let url = `/api/v3/campaigns/${this.campaignId}/`,
          errorMessage = `could not load the filters`

        url = callType === 'get' ? url + 'selected_groups' : url + 'update_groups'

        axiosConfig[callType](url, params)
          .then((response) => {
            const data = response.data
            if (isObject(data)) {
              this.setGroupsData(data)
              !this.hasAdFilters && this.getTotalCount()
              errorMessage = ''
              this.setUpdating(data)
            }
            this.errorMessage = errorMessage
          })
          .catch((error) => {
            errorMessage = `${errorMessage}. ${error}`
            this.errorMessage = errorMessage
            this.updating = false
          })
      },

      loadGroups() {
        this.updating = true
        this.groupsCall('get')
      },

      setUpdating(data) {
        this.$emit('updateAlerts', null, {})
        this.updating = false
      },

      setErrorMessage(message) {
        this.errorMessage = message
      },

      setGroupsData(data) {
        this.allFilters = data.groups_with_selected
        this.shouldFilterGroup(data.groups_with_selected)
        this.merchantSegmentIds = data.merchant_segment_ids
        this.merchantTagIds = data.merchant_tag_ids
        const subConfig = data.sub_configuration
        this.subConfigMax = subConfig ? subConfig.max_date : null
        this.subConfigMin = subConfig ? subConfig.min_date : null
      },

      setSelectedDateRange(dateRange) {
        this.selectedDateRange = dateRange
      },

      setSelectedItem(item) {
        this.$emit('updateData', 'openGroupItem', item)
        this.sectionOpen = true
      },

      shouldFilterGroup(groups, groupName = 'carrier') {
        let groupFilters = groups
        if (this.hideCarriersGroup) { groupFilters = groupFilters?.filter(({name}) => name !== groupName) }
        this.groupsWithSelected = groupFilters
      },

      toggleWidget(event) {
        event.stopPropagation()
        this.slideDown = !this.slideDown
        if (!this.slideDown) { this.closeSection() }
      },

      updateGroups(dataToUpdate) {
        const groupsData = { campaign: dataToUpdate }
        if (this.isCampaign) {
          this.updating = true
          this.groupsCall('patch', groupsData)
        } else {
          this.$emit('updateScope', dataToUpdate)
        }
      },
    },

    created() {
      this.isCampaign = this.campaignType === 'Campaign'

      if (this.isCampaign) {
        this.loadGroups()
      } else {
        this.merchantSegmentIds = this.scopeSelectedSegmentIds
        this.merchantTagIds = this.scopeMerchantTagIds
        this.groupsWithSelected = this.scopeSelectedGroups
        this.shouldFilterGroup(this.scopeSelectedGroups)
      }
    },

    computed: {
      subConfigDate: function() {
        const hasMinMax = this.subConfigMax && this.subConfigMin
        return hasMinMax ? `${this.subConfigMin} to ${this.subConfigMax}` : null
      },

      widgetOpenClass: function() {
        let openClass = this.sectionOpen ? 'open filters' : 'filters'
        return this.slideDown ? `slide-down ${openClass}` : `close ${openClass}`
      },

      hasAdFilters() {
        return this.adFilters?.groups?.length > 0
      },

      filterGroups() {
        return this.adFilters?.groups
      }
    },

    watch: {
      hideCarriersGroup: function() {
        this.shouldFilterGroup(this.allFilters)
      },

      onlySelectedGroups: function() {
        this.$emit('updateData', 'noFiltersSelected', this.onlySelectedGroups?.length === 0)
      },

      scopeMerchantTagIds: function() {
        this.merchantTagIds = this.scopeMerchantTagIds
      },

      scopeSelectedGroups: function() {
        this.groupsWithSelected = this.scopeSelectedGroups
        this.shouldFilterGroup(this.scopeSelectedGroups)
      },

      scopeSelectedSegmentIds: function() {
        this.merchantSegmentIds = this.scopeSelectedSegmentIds
      },

      selectedView: function() {
        const counts = this.selectedView === 'email' ? this.emailTotal : this.smsTotal
        this.configTotalCounts(counts)
      },

      adFilters: function(value) {
        let dataToUpdate = {
          merchant_segment_ids: [],
          category_list: ''
        }
        if (!!value) {
          dataToUpdate = {
            merchant_segment_ids: value.segments,
            category_list: value.tags.join(',')
          }
          this.totalCount = value.totalRecipients
        }
        this.updateGroups(dataToUpdate)
      }
    }
  }
</script>

<style scoped>
.group-widget-section.with-ad-filter .group-widget {
  border-color: #a4a4a4;
  pointer-events: none;
}

.group-widget-section.with-ad-filter .group-widget .filters-header {
  background-color: #a4a4a4;
}
</style>
